import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthCtx } from '../context/AuthContext';
import EmployerNav from '../components/employer/EmployerNav';
import EMTLayout from '../components/EMTLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import {
  FEEDBACK_PAGE_SIZE,
  fetchFeedbackForUserPage,
  submitFeedback,
  type FeedbackCategory,
  type UserFeedbackItem,
} from '../lib/firestore/feedbackRepo';
import { friendly } from '../lib/errors';
import { practiceFeedbackPrefix } from '../lib/practiceAccount';
import { Loader2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import EmptyState from '../components/ui/EmptyState';
import Skeleton from '../components/ui/skeleton';
import RequiredMark from '../components/ui/RequiredMark';

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature idea' },
  { value: 'ui', label: 'UI/design feedback' },
  { value: 'account', label: 'Account issue' },
  { value: 'other', label: 'Other' },
];

function roleForSubmit(profile: unknown): 'emt' | 'employer' | null {
  const r = profile && typeof profile === 'object' ? (profile as { role?: string }).role : null;
  if (r === 'employer' || r === 'emt') return r;
  return null;
}

function categoryLabel(value: FeedbackCategory): string {
  return CATEGORY_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

function formatSubmittedAt(value: unknown): string {
  if (!value) return '';
  try {
    const d = (value as { toDate?: () => Date }).toDate
      ? (value as { toDate: () => Date }).toDate()
      : new Date(value as string | number);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '';
  }
}

/** Maps stored status to a user-facing label; defaults to “Submitted”. */
function feedbackStatusLabel(status: string): string {
  const s = (status || '').toLowerCase();
  if (s === 'new' || !s) return 'Submitted';
  if (s === 'submitted') return 'Submitted';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

type FeedbackPageProps = {
  /**
   * When true, the page is rendered as a child of the EMT `EMTLayout` route in root `App.tsx`
   * and must not wrap content in another `EMTLayout` (avoids double shell + broken outlets).
   */
  emtInLayout?: boolean;
};

export default function FeedbackPage({ emtInLayout = false }: FeedbackPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading: authLoading } = useAuthCtx();

  const isEmployerPath = location.pathname.startsWith('/employer/feedback');

  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [pageArea, setPageArea] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [feedbackTab, setFeedbackTab] = useState<'send' | 'submitted'>('send');
  const [submissions, setSubmissions] = useState<UserFeedbackItem[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsLoadingMore, setSubmissionsLoadingMore] = useState(false);
  const [submissionsHasMore, setSubmissionsHasMore] = useState(false);
  const [submissionsCursor, setSubmissionsCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [submissionsError, setSubmissionsError] = useState<string | null>(null);
  const [submissionsRefreshKey, setSubmissionsRefreshKey] = useState(0);

  const loadSubmissionsFirstPage = useCallback(async () => {
    if (!user) return;
    setSubmissionsLoading(true);
    setSubmissionsError(null);
    setSubmissions([]);
    setSubmissionsHasMore(false);
    setSubmissionsCursor(null);
    try {
      const page = await fetchFeedbackForUserPage(user.uid, FEEDBACK_PAGE_SIZE, null);
      setSubmissions(page.items);
      setSubmissionsHasMore(page.hasMore);
      setSubmissionsCursor(page.endCursor);
    } catch (err) {
      setSubmissionsError(friendly(err, 'Could not load your feedback.'));
      setSubmissions([]);
      setSubmissionsHasMore(false);
      setSubmissionsCursor(null);
    } finally {
      setSubmissionsLoading(false);
    }
  }, [user]);

  const loadMoreSubmissions = useCallback(async () => {
    if (!user || submissionsLoadingMore || !submissionsHasMore || !submissionsCursor) return;
    setSubmissionsLoadingMore(true);
    try {
      const page = await fetchFeedbackForUserPage(user.uid, FEEDBACK_PAGE_SIZE, submissionsCursor);
      setSubmissions((prev) => [...prev, ...page.items]);
      setSubmissionsHasMore(page.hasMore);
      setSubmissionsCursor(page.endCursor);
    } catch (err) {
      setSubmissionsError(friendly(err, 'Could not load more feedback.'));
    } finally {
      setSubmissionsLoadingMore(false);
    }
  }, [user, submissionsLoadingMore, submissionsHasMore, submissionsCursor]);

  useEffect(() => {
    if (authLoading || !user || feedbackTab !== 'submitted') return;
    void loadSubmissionsFirstPage();
  }, [authLoading, user, feedbackTab, submissionsRefreshKey, loadSubmissionsFirstPage]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [authLoading, user, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const t = title.trim();
    const m = message.trim();
    if (!t || !m) {
      setError('Please enter a short summary and a detailed message.');
      return;
    }

    if (!user) {
      setError('You must be signed in to send feedback.');
      return;
    }

    setSubmitting(true);
    try {
      await submitFeedback({
        userId: user.uid,
        userEmail: user.email ?? '',
        userRole: roleForSubmit(profile),
        category,
        // From a practice company, the super admin sees where it came from in the title.
        title: `${practiceFeedbackPrefix(profile)}${t}`.slice(0, 280),
        message: m,
        pageArea: pageArea.trim(),
      });
      setSuccess(true);
      setTitle('');
      setMessage('');
      setPageArea('');
      setCategory('bug');
      setSubmissionsRefreshKey((k) => k + 1);
    } catch (err) {
      setError(friendly(err, 'Could not send feedback. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--surface-muted)] text-[var(--text-secondary)]">
        Loading…
      </div>
    );
  }

  const form = (
    <div className="max-w-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8 rr-page-enter">
      <Card className="rr-card-hover border-[var(--border-default)] shadow-sm bg-[var(--surface-card)]">
        <CardHeader className="space-y-4">
          <div
            className="flex flex-wrap gap-2 border-b border-[var(--border-default)] pb-4 -mb-2"
            role="tablist"
            aria-label="Feedback sections"
          >
            <button
              type="button"
              role="tab"
              aria-selected={feedbackTab === 'send'}
              id="feedback-tab-send"
              className={`rounded-lg px-4 py-2 text-sm font-medium min-h-[2.25rem] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/80 ${
                feedbackTab === 'send'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'bg-[var(--surface-muted)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]'
              }`}
              onClick={() => setFeedbackTab('send')}
            >
              Send feedback
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={feedbackTab === 'submitted'}
              id="feedback-tab-submitted"
              aria-controls="feedback-panel-submitted"
              className={`rounded-lg px-4 py-2 text-sm font-medium min-h-[2.25rem] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/80 ${
                feedbackTab === 'submitted'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'bg-[var(--surface-muted)] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]'
              }`}
              onClick={() => setFeedbackTab('submitted')}
            >
              Submitted feedback
            </button>
          </div>
          <div>
            <CardTitle className="text-xl">
              {feedbackTab === 'send' ? 'Send feedback' : 'Your submitted feedback'}
            </CardTitle>
            <CardDescription>
              {feedbackTab === 'send'
                ? 'Tell us about bugs, ideas, or anything that would make Arevina better for you.'
                : 'Only feedback you sent from this account is shown here.'}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {feedbackTab === 'submitted' && (
            <div id="feedback-panel-submitted" role="tabpanel" aria-labelledby="feedback-tab-submitted">
              {submissionsLoading && (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="rounded-lg border border-[var(--border-default)] p-4 space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-32" />
                      <Skeleton className="h-16 w-full" />
                    </div>
                  ))}
                </div>
              )}
              {!submissionsLoading && submissionsError && (
                <p className="text-sm text-red-600" role="alert">
                  {submissionsError}
                </p>
              )}
              {!submissionsLoading && !submissionsError && submissions.length === 0 && (
                <EmptyState title="No feedback submitted yet" />
              )}
              {!submissionsLoading && !submissionsError && submissions.length > 0 && (
                <>
                  <ul className="space-y-4">
                    {submissions.map((item) => (
                      <li
                        key={item.id}
                        className="rounded-lg border border-[var(--border-default)] bg-[var(--surface-muted)]/50 p-4 text-left"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2 gap-y-1">
                          <h3 className="font-semibold text-[var(--text-primary)] text-sm sm:text-base pr-2">
                            {item.title || 'Feedback'}
                          </h3>
                          <Badge
                            variant="secondary"
                            className="shrink-0 bg-[var(--surface-card)] text-[var(--text-secondary)] border border-[var(--border-default)] text-xs font-medium"
                          >
                            {feedbackStatusLabel(item.status)}
                          </Badge>
                        </div>
                        <p className="mt-1 text-xs text-[var(--text-muted)]">
                          {formatSubmittedAt(item.createdAt)}
                          <span className="text-[var(--text-muted)]"> · </span>
                          <span>{categoryLabel(item.category)}</span>
                        </p>
                        <p className="mt-3 text-sm text-[var(--text-secondary)] whitespace-pre-wrap break-words">
                          {item.message}
                        </p>
                        {item.pageArea.trim() ? (
                          <p className="mt-2 text-xs text-[var(--text-muted)]">
                            <span className="font-medium text-[var(--text-secondary)]">Area: </span>
                            {item.pageArea}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {submissionsHasMore && (
                    <div className="flex justify-center pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-[var(--border-strong)] text-[var(--text-secondary)] min-h-[2.5rem]"
                        disabled={submissionsLoadingMore}
                        onClick={() => void loadMoreSubmissions()}
                      >
                        {submissionsLoadingMore ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading…
                          </>
                        ) : (
                          'Load more'
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {feedbackTab === 'send' && (
          <form onSubmit={handleSubmit} className="space-y-5" aria-labelledby="feedback-tab-send">
            <div className="space-y-2">
              <Label htmlFor="feedback-category">Category</Label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as FeedbackCategory)}
              >
                <SelectTrigger id="feedback-category" className="w-full">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-title">
                <RequiredMark />
                Short summary
              </Label>
              <Input
                id="feedback-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="One line describing your feedback"
                maxLength={280}
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-message">
                <RequiredMark />
                Details
              </Label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What happened? What would you like to see instead?"
                rows={6}
                maxLength={10000}
                className="min-h-[120px] resize-y"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-page">Page or area (optional)</Label>
              <Input
                id="feedback-page"
                value={pageArea}
                onChange={(e) => setPageArea(e.target.value)}
                placeholder="e.g. Employer dashboard, job search filters"
                maxLength={500}
                autoComplete="off"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            {success && (
              <p className="text-sm text-green-700" role="status">
                Thanks, your feedback was submitted.
              </p>
            )}

            <Button
              type="submit"
              className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                'Submit feedback'
              )}
            </Button>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );

  if (isEmployerPath) {
    return (
      <div className="min-h-screen bg-[var(--surface-muted)]">
        <EmployerNav />
        {form}
      </div>
    );
  }

  if (emtInLayout) {
    return form;
  }

  return <EMTLayout>{form}</EMTLayout>;
}
